/**
 * プラン別ストレージ上限のフロント側ミラー。
 *
 * 値は apps/api/src/lib/plan-limits.ts と同期させる必要がある（真実の源は API 側）。
 * 将来 @terafile/shared に昇格させて重複を解消する。
 */

import { getPlanLabel as getPlanLabelFromLabels } from './labels.js'

type Plan = 'FREE' | 'PRO' | 'EPHEMERAL'

const GIB = 1024 ** 3
const TIB = 1024 ** 4

const BASE_STORAGE_BYTES: Record<Plan, number> = {
  EPHEMERAL: 8 * GIB,
  FREE: 70 * GIB,
  PRO: 2 * TIB,
}

export function getEffectiveStorageLimitBytes(plan: Plan, addonStorageTb: number): number {
  return BASE_STORAGE_BYTES[plan] + addonStorageTb * TIB
}

export function getPlanLabel(plan: Plan): string {
  return getPlanLabelFromLabels(plan)
}

/**
 * 0〜100 の整数で使用率を返す。上限を超えた場合は 100 にクランプ。
 */
export function getStorageUsagePercent(usedBytes: number, limitBytes: number): number {
  if (limitBytes <= 0) return 0
  return Math.min(100, Math.round((usedBytes / limitBytes) * 100))
}

/**
 * 使用率に応じて段階を返す。
 *   ok      : ~70%   通常表示
 *   warning : 70-90% 黄色寄り、控えめな警告
 *   danger  : 90%+   赤、強い警告（アップグレード誘導）
 */
export type StorageLevel = 'ok' | 'warning' | 'danger'

export function getStorageLevel(percent: number): StorageLevel {
  if (percent >= 90) return 'danger'
  if (percent >= 70) return 'warning'
  return 'ok'
}

export function formatBytesShort(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < GIB) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  if (bytes < TIB) return `${(bytes / GIB).toFixed(1)} GB`
  return `${(bytes / TIB).toFixed(2)} TB`
}
